import { useState, useCallback } from 'react';

/**
 * Custom hook to manage AI suggestion and editing states in a centralized way
 * Consolidates suggestion-related state variables including text suggestions,
 * assessment suggestions, mode change suggestions, and editing states
 */
const useSuggestionState = (initialState = {}) => {
  const [suggestionState, setSuggestionState] = useState({
    // Current text suggestion from AI
    current: null,
    
    // Assessment suggestion from AI
    assessment: null,
    
    // Mode change suggestion from AI
    modeChange: null,
    
    // Editing states
    isEditing: false,
    isEditingManual: false,
    editText: '',
    
    // Therapist input text
    therapistInput: '',
    
    // Goal proposal trigger for re-renders
    goalProposalTrigger: 0,
    localPendingGoalProposal: null,
    
    ...initialState
  });

  // Set current text suggestion
  const setCurrentSuggestion = useCallback((suggestion) => {
    setSuggestionState(prev => ({
      ...prev,
      current: suggestion
    }));
  }, []);

  // Set assessment suggestion
  const setAssessmentSuggestion = useCallback((assessment) => {
    setSuggestionState(prev => ({
      ...prev,
      assessment: assessment
    }));
  }, []);

  // Set mode change suggestion
  const setModeChangeSuggestion = useCallback((modeChange) => {
    setSuggestionState(prev => ({
      ...prev,
      modeChange: modeChange
    }));
  }, []);

  // Set editing state
  const setEditingState = useCallback((field, value) => {
    setSuggestionState(prev => ({
      ...prev,
      [field]: value
    }));
  }, []);

  // Set therapist input text
  const setTherapistInput = useCallback((text) => {
    setSuggestionState(prev => ({
      ...prev,
      therapistInput: text
    }));
  }, []);

  // Clear all suggestions
  const clearAllSuggestions = useCallback(() => {
    setSuggestionState(prev => ({
      ...prev,
      current: null,
      assessment: null,
      modeChange: null,
      isEditing: false,
      isEditingManual: false,
      editText: ''
    }));
  }, []);

  // Clear text suggestion specifically
  const clearTextSuggestion = useCallback(() => {
    setSuggestionState(prev => ({
      ...prev,
      current: null,
      isEditing: false,
      isEditingManual: false,
      editText: ''
    }));
  }, []);

  // Clear assessment suggestion
  const clearAssessmentSuggestion = useCallback(() => {
    setSuggestionState(prev => ({
      ...prev,
      assessment: null
    }));
  }, []);

  // Clear mode change suggestion
  const clearModeChangeSuggestion = useCallback(() => {
    setSuggestionState(prev => ({
      ...prev,
      modeChange: null
    }));
  }, []);

  // Start editing suggestion
  const startEditingSuggestion = useCallback(() => {
    setSuggestionState(prev => ({
      ...prev,
      isEditingManual: true,
      editText: prev.current || ''
    }));
  }, []);

  // Cancel editing suggestion
  const cancelEditingSuggestion = useCallback(() => {
    setSuggestionState(prev => ({
      ...prev,
      isEditingManual: false,
      editText: ''
    }));
  }, []);

  // Update multiple suggestion states at once
  const updateSuggestionState = useCallback((updates) => {
    setSuggestionState(prev => ({
      ...prev,
      ...updates
    }));
  }, []);

  // Increment goal proposal trigger
  const triggerGoalProposalUpdate = useCallback(() => {
    setSuggestionState(prev => ({
      ...prev,
      goalProposalTrigger: prev.goalProposalTrigger + 1
    }));
  }, []);

  // Set local pending goal proposal
  const setLocalPendingGoalProposal = useCallback((proposal) => {
    setSuggestionState(prev => ({
      ...prev,
      localPendingGoalProposal: proposal
    }));
  }, []);

  // Check if any suggestion is active
  const hasAnySuggestion = suggestionState.current || 
                          suggestionState.assessment || 
                          suggestionState.modeChange;

  // Check if editing any suggestion
  const isEditingAnySuggestion = suggestionState.isEditing || 
                                suggestionState.isEditingManual;

  return {
    suggestionState,
    setCurrentSuggestion,
    setAssessmentSuggestion,
    setModeChangeSuggestion,
    setEditingState,
    setTherapistInput,
    clearAllSuggestions,
    clearTextSuggestion,
    clearAssessmentSuggestion,
    clearModeChangeSuggestion,
    startEditingSuggestion,
    cancelEditingSuggestion,
    updateSuggestionState,
    triggerGoalProposalUpdate,
    setLocalPendingGoalProposal,
    hasAnySuggestion,
    isEditingAnySuggestion
  };
};

export default useSuggestionState;